/**
 * API service for the Assignments page (Tab 2 of the Station Assignment workflow).
 *
 * Mirrors the `getShiftRoster` pattern: reads `stationCode` from the Redux
 * store, builds a typed request, calls `apiRequest` (which is routed through
 * `apiMiddleware` to `MockDataService.getStationAssignment` in local environments),
 * and dispatches `showError` on any failure while returning `EMPTY_RESPONSE`.
 */
import { store } from '../../../store';
import { selectStationCode } from '../../../store/slices/shiftPlanSlice';
import { selectStationAssignmentPlanId } from '../../../store/slices/stationAssignmentSlice';
import { apiRequest } from '../../../services/apiMiddleware';
import { API_GATEWAY_PROCESS_NAME } from '../../../utils/constants';
import { showError } from '../../../store/slices/errorSlice';
import { getUserName } from '../../../services/boson/bosonClient';
import {
  IS_VALID_STATION_ASSIGNMENT_OVERRIDE_API,
  PUBLISH_STATION_ASSIGNMENT_API,
  logOperationalMetric,
  MetricType,
  RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE,
} from '../../../utils/metric-utils';
import { AssociateOverrideEntry, EMPTY_RESPONSE, GetStationAssignmentsRequest, GetStationAssignmentsResponse, IsValidStationAssignmentOverrideRequest, IsValidStationAssignmentOverrideResponse, PublishStationAssignmentRequest, StationAssignmentResponse } from './models';

/**
 * Fetch station assignment data for the given date, shift, and plan.
 *
 * @param ofdDate The selected date (OFD date)
 * @param clockWindowName The shift window (e.g. DAY or NIGHT)
 * @param planId The station assignment plan ID
 * @returns Promise with the GetStationAssignmentsResponse; returns EMPTY_RESPONSE on any failure
 */
export const getStationAssignments = async (
  ofdDate: string,
  clockWindowName: string,
  planId: string,
): Promise<GetStationAssignmentsResponse> => {
  try {
    const state = store.getState();
    const stationCode = selectStationCode(state);

    if (!stationCode) {
      store.dispatch(showError({
        title: 'Warehouse ID Required',
        message: 'Please select a valid warehouse ID from the dropdown',
      }));
      return EMPTY_RESPONSE;
    }

    const requestBody: GetStationAssignmentsRequest = {
      nodeId: stationCode,
      warehouseId: stationCode,
      ofdDate,
      clockWindowName,
      planId,
    };

    const requestPayload = {
      resourcePath: '/getStationAssignment',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };

    const response = await apiRequest(requestPayload);

    if (response.status && response.status >= 400) {
      store.dispatch(showError({
        title: 'Failed to fetch assignments data',
        message: response.errorResponse || 'Unable to fetch assignments data. Please try again later.',
      }));
      return EMPTY_RESPONSE;
    }

    return response.data as GetStationAssignmentsResponse;
  } catch (error: any) {
    console.error('Error fetching assignments data:', error);
    store.dispatch(showError({
      title: 'Failed to fetch assignments data',
      message: error.errorResponse || error.message || 'Unable to fetch assignments data. Please try again later.',
    }));
    return EMPTY_RESPONSE;
  }
};

/**
 * Validate whether associate override(s) are allowed.
 * Accepts 1 entry for a single move, 2 entries for a swap.
 *
 * @param overrides Array of AssociateOverrideEntry (1 for move, 2 for swap)
 * @returns Promise with validation errors array; empty array means valid
 */
export const isValidStationAssignmentOverride = async (
  overrides: AssociateOverrideEntry[],
): Promise<IsValidStationAssignmentOverrideResponse> => {
  const startTime = Date.now();
  const state = store.getState();
  const stationCode = selectStationCode(state) || '';

  try {
    const planId = selectStationAssignmentPlanId(state);

    if (!planId) {
      store.dispatch(showError({
        title: 'Plan ID Required',
        message: 'No Plan ID related to station assignment exists',
      }));
      return { validationErrors: [] };
    }

    const requestBody: IsValidStationAssignmentOverrideRequest = {
      nodeId: stationCode,
      warehouseId: stationCode,
      planId,
      overrides,
    };

    const requestPayload = {
      resourcePath: '/isValidStationAssignmentOverride',
      httpMethod: 'post',
      processName: API_GATEWAY_PROCESS_NAME,
      requestBody,
    };

    const response = await apiRequest(requestPayload);

    if (response.status && response.status >= 400) {
      logOperationalMetric(
        startTime,
        stationCode,
        RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE,
        IS_VALID_STATION_ASSIGNMENT_OVERRIDE_API,
        MetricType.FAILED,
      );
      store.dispatch(showError({
        title: 'Failed to validate override',
        message: response.errorResponse || 'Unable to validate override. Please try again later.',
      }));
      return { validationErrors: [] };
    }

    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE,
      IS_VALID_STATION_ASSIGNMENT_OVERRIDE_API,
      MetricType.SUCCESS,
    );

    return response.data as IsValidStationAssignmentOverrideResponse;
  } catch (error: any) {
    console.error('Error validating override:', error);

    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE,
      IS_VALID_STATION_ASSIGNMENT_OVERRIDE_API,
      MetricType.FAILED,
      error,
    );

    store.dispatch(showError({
      title: 'Failed to validate override',
      message: error.errorResponse || error.message || 'Unable to validate override. Please try again later.',
    }));
    return { validationErrors: [] };
  }
};

/**
 * Publish the station assignment plan.
 *
 * Mirrors the `publishShiftPlan` pattern: reads stationCode from Redux,
 * gets the current user, and calls the `/publishStationAssignment` endpoint
 * with the plan ID, assignment state (with all swaps applied), and APPROVED status.
 *
 * @param planId The station assignment plan ID to publish
 * @param stationAssignment The full assignment state including all swaps made by the user
 * @returns Promise that resolves on success; throws on failure
 */
export const publishStationAssignment = async (
  planId: string,
  stationAssignment: StationAssignmentResponse,
): Promise<void> => {
  const startTime = Date.now();
  const state = store.getState();
  const stationCode = selectStationCode(state);
  const userId: string = (await getUserName()) as string;

  if (!stationCode) {
    store.dispatch(showError({
      title: 'Warehouse ID Required',
      message: 'Please select a valid warehouse ID from the dropdown',
    }));
    return;
  }

  const requestBody: PublishStationAssignmentRequest = {
    planId,
    nodeId: stationCode,
    warehouseId: stationCode,
    updatedBy: userId,
    stationAssignment,
  };

  const requestPayload = {
    resourcePath: '/publishStationAssignment',
    httpMethod: 'post',
    processName: API_GATEWAY_PROCESS_NAME,
    requestBody,
  };

  try {
    const response = await apiRequest(requestPayload);

    if (response.status && response.status >= 400) {
      throw new Error(response.errorResponse || 'Failed to publish station assignment plan');
    }

    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE,
      PUBLISH_STATION_ASSIGNMENT_API,
      MetricType.SUCCESS,
    );
  } catch (error: any) {
    logOperationalMetric(
      startTime,
      stationCode,
      RCC_SHIFT_PLAN_STATION_ASSIGNMENT_PAGE,
      PUBLISH_STATION_ASSIGNMENT_API,
      MetricType.FAILED,
      error,
    );
    throw error;
  }
};
